import type { Locale } from "./types";

type LocalizedText = Record<Locale, string>;

export type DataRoomStage = {
  id: "intake" | "dry_run" | "preview" | "confirm" | "evidence_record" | "handoff";
  title: LocalizedText;
  body: LocalizedText;
  assetId: string;
};

export type DataRoomModuleStory = {
  key:
    | "bas_and_balances"
    | "ledger_and_journals"
    | "vat_reports"
    | "bank_sources"
    | "ap_ar"
    | "sie_migration"
    | "fixed_assets"
    | "report_artifacts";
  title: LocalizedText;
  body: LocalizedText;
  tag: LocalizedText;
  assetId: string;
};

export type DeliveryPathStory = {
  key: "self_orchestrated_agent" | "hybrid" | "human_assisted" | "review_evidence";
  title: LocalizedText;
  body: LocalizedText;
  assetId: string;
};

export type DataRoomWorkspacePanel = {
  id: "records" | "agent" | "review";
  iconLabel: LocalizedText;
  kicker: LocalizedText;
  title: LocalizedText;
  items: Record<Locale, string[]>;
  footer?: LocalizedText;
  capacity?: {
    label: LocalizedText;
    title: LocalizedText;
    body: LocalizedText;
  };
  review?: {
    status: LocalizedText;
    body: LocalizedText;
  };
};

export type DataRoomConfirmedOutput = {
  title: LocalizedText;
  body: LocalizedText;
};

export type DataRoomHandoffContent = {
  eyebrow: LocalizedText;
  title: LocalizedText;
  lead: LocalizedText;
  boundary: LocalizedText;
  relationship: LocalizedText;
  illustration: {
    ariaLabel: LocalizedText;
    sources: {
      kicker: LocalizedText;
      title: LocalizedText;
      items: Record<Locale, string[]>;
    };
    workspace: {
      kicker: LocalizedText;
      title: LocalizedText;
      statuses: Record<Locale, string[]>;
      panels: DataRoomWorkspacePanel[];
      evidenceTrail: {
        kicker: LocalizedText;
        title: LocalizedText;
        items: Record<Locale, string[]>;
      };
    };
    output: {
      kicker: LocalizedText;
      title: LocalizedText;
      items: DataRoomConfirmedOutput[];
    };
  };
  process: {
    eyebrow: LocalizedText;
    title: LocalizedText;
  };
  stages: DataRoomStage[];
  workstreams: {
    eyebrow: LocalizedText;
    title: LocalizedText;
    lead: LocalizedText;
  };
  modules: DataRoomModuleStory[];
  delivery: {
    eyebrow: LocalizedText;
    title: LocalizedText;
  };
  deliveryPaths: DeliveryPathStory[];
  controlBoundary: {
    eyebrow: LocalizedText;
    title: LocalizedText;
    body: LocalizedText;
  };
  evidenceRules: Record<Locale, string[]>;
};

export const dataRoomHandoffContent: DataRoomHandoffContent = {
  eyebrow: {
    sv: "Data Rooms",
    en: "Data Rooms"
  },
  title: {
    sv: "Säkra Data Rooms för bokföringsunderlag, agentarbete och granskning",
    en: "Secure accounting Data Rooms for records, agent work, and review"
  },
  lead: {
    sv:
      "Varje bolag arbetar i sitt eget säkra Data Room, där bokföringsunderlag, bankdata, rapporter, frågor och stödjande evidens hålls samman. AI-bokföringsagenter kan läsa tillgängligt material, förbereda torrkörningar, identifiera avvikelser och skapa rapportartefakter, medan kunder och servicegranskare behåller kontrollen över bekräftelse och nästa steg.",
    en:
      "Each company works inside its own secure Data Room, where accounting records, bank data, reports, questions, and supporting evidence stay connected. AI accounting agents can read the available material, prepare dry-runs, identify discrepancies, and create report artifacts, while customers and service reviewers retain control over confirmation and next steps."
  },
  boundary: {
    sv:
      "Inget bokförs, lämnas in, betalas eller skickas direkt från den publika webbplatsen. Bokföringsåtgärder fortsätter endast i den avgränsade bolagsytan efter nödvändiga kontroller och bekräftelser.",
    en:
      "Nothing is posted, filed, paid, or submitted directly from the public website. Accounting actions continue only inside the scoped company workspace after the required checks and confirmations."
  },
  relationship: {
    sv:
      "Bokföringsagenten förklarar vem som förbereder arbetet. Data Rooms förklarar var arbetet förbereds, granskas, bekräftas och sparas som evidens.",
    en:
      "The Accounting Agent section explains who prepares the work. The Data Rooms section explains where the work is prepared, reviewed, confirmed, and retained as evidence."
  },
  illustration: {
    ariaLabel: {
      sv:
        "DynamicBooks Data Room-arbetsflöde där bokföringskällor går in i en bolagsyta, där bokföringsagenter förbereder torrkörningar, granskare kontrollerar avvikelser och omfattning, och bekräftade rapporter och evidens förblir spårbara.",
      en:
        "DynamicBooks Data Room workflow showing accounting sources entering a company workspace, where accounting agents prepare dry-runs, reviewers check discrepancies and scope, and confirmed reports and evidence remain traceable."
    },
    sources: {
      kicker: {
        sv: "BOLAGSUNDERLAG",
        en: "COMPANY INPUTS"
      },
      title: {
        sv: "Bokföringskällor och krav",
        en: "Accounting sources and requirements"
      },
      items: {
        sv: [
          "Bankkällor",
          "Kvitton",
          "Leverantörsfakturor",
          "Kundfakturor",
          "Momsperioder",
          "SIE-filer",
          "Rapporter",
          "Frågor"
        ],
        en: [
          "Bank sources",
          "Receipts",
          "Supplier invoices",
          "Customer invoices",
          "VAT periods",
          "SIE files",
          "Reports",
          "Questions"
        ]
      }
    },
    workspace: {
      kicker: {
        sv: "BOLAGSYTA",
        en: "COMPANY WORKSPACE"
      },
      title: {
        sv: "DynamicBooks Data Room",
        en: "DynamicBooks Data Room"
      },
      statuses: {
        sv: ["Avgränsad", "Spårbar"],
        en: ["Scoped", "Traceable"]
      },
      panels: [
        {
          id: "records",
          iconLabel: {
            sv: "01",
            en: "01"
          },
          kicker: {
            sv: "LÄS",
            en: "READ"
          },
          title: {
            sv: "Bolagets underlag",
            en: "Company records"
          },
          items: {
            sv: [
              "Kontoplan och momskoder",
              "Räkenskapsår och saldon",
              "Huvudbok och verifikationer",
              "Banktransaktioner och importer",
              "Rapporter och befintliga artefakter"
            ],
            en: [
              "Chart of accounts and VAT codes",
              "Fiscal years and balances",
              "Ledger and journal records",
              "Bank transactions and imports",
              "Reports and existing artifacts"
            ]
          },
          footer: {
            sv: "Bolagsspecifik bokföringskontext",
            en: "Company-specific accounting context"
          }
        },
        {
          id: "agent",
          iconLabel: {
            sv: "AI",
            en: "AI"
          },
          kicker: {
            sv: "FÖRBERED",
            en: "PREPARE"
          },
          title: {
            sv: "Bokföringsagentens arbetsyta",
            en: "Accounting agent workspace"
          },
          items: {
            sv: [
              "Förbered bokföringsförslag",
              "Kör moms- och rapportkontroller",
              "Identifiera avvikelser",
              "Förhandsvisa SIE- och importresultat",
              "Skapa rapport- och evidensutkast"
            ],
            en: [
              "Prepare posting suggestions",
              "Run VAT and report checks",
              "Identify discrepancies",
              "Preview SIE and import results",
              "Create report and evidence drafts"
            ]
          },
          capacity: {
            label: {
              sv: "Agent Credits",
              en: "Agent Credits"
            },
            title: {
              sv: "Förväntad körkapacitet",
              en: "Expected processing capacity"
            },
            body: {
              sv:
                "Användning registreras mot förberedelse, validering, rapportgenerering och andra agentassisterade arbetsflöden.",
              en:
                "Usage is recorded against preparation, validation, report generation, and other agent-assisted workflows."
            }
          }
        },
        {
          id: "review",
          iconLabel: {
            sv: "OK",
            en: "OK"
          },
          kicker: {
            sv: "GRANSKA",
            en: "REVIEW"
          },
          title: {
            sv: "Kontroller och omfattningsstyrning",
            en: "Checks and scope control"
          },
          items: {
            sv: [
              "Granska valideringsresultat",
              "Kontrollera avvikelser och saknad evidens",
              "Bekräfta ansvarsnivå",
              "Styr komplexa bokföringsfall rätt",
              "Godkänn nästa arbetsflödessteg"
            ],
            en: [
              "Review validation results",
              "Check exceptions and missing evidence",
              "Confirm responsibility level",
              "Route complex accounting cases",
              "Approve the next workflow step"
            ]
          },
          review: {
            status: {
              sv: "Klar för bekräftelse",
              en: "Ready for confirmation"
            },
            body: {
              sv: "Ingen bokföringsändring har bokförts",
              en: "No accounting change has been posted"
            }
          }
        }
      ],
      evidenceTrail: {
        kicker: {
          sv: "EVIDENSSPÅR",
          en: "EVIDENCE TRAIL"
        },
        title: {
          sv: "Beslut och artefakter förblir kopplade",
          en: "Decisions and artifacts remain connected"
        },
        items: {
          sv: [
            "Förhandsresultat",
            "Valideringskontroller",
            "Granskningsnoteringar",
            "Bekräftelser",
            "Genererade filer",
            "Kvittenser"
          ],
          en: [
            "Preview results",
            "Validation checks",
            "Review notes",
            "Confirmations",
            "Generated files",
            "Receipts"
          ]
        }
      }
    },
    output: {
      kicker: {
        sv: "BEKRÄFTAT RESULTAT",
        en: "CONFIRMED OUTPUT"
      },
      title: {
        sv: "Bokföringsmaterial förberett för nästa godkända steg",
        en: "Accounting material prepared for the next approved step"
      },
      items: [
        {
          title: {
            sv: "Bokföring",
            en: "Bookkeeping"
          },
          body: {
            sv: "Förberedda bokningar och matchningsunderlag",
            en: "Prepared postings and matching material"
          }
        },
        {
          title: {
            sv: "Moms",
            en: "VAT"
          },
          body: {
            sv: "Validerade momsrapporter och artefakter",
            en: "Validated VAT reports and artifacts"
          }
        },
        {
          title: {
            sv: "Rapporter",
            en: "Reports"
          },
          body: {
            sv: "Finansiellt och operativt rapportmaterial",
            en: "Financial and management report material"
          }
        },
        {
          title: {
            sv: "Migrering",
            en: "Migration"
          },
          body: {
            sv: "SIE-förhandsvisningar, kontroller och avvikelsespår",
            en: "SIE previews, checks, and discrepancy records"
          }
        },
        {
          title: {
            sv: "Evidens",
            en: "Evidence"
          },
          body: {
            sv: "Filer, beslut, bekräftelser och kvittenser",
            en: "Files, decisions, confirmations, and receipts"
          }
        }
      ]
    }
  },
  process: {
    eyebrow: {
      sv: "SÅ FUNGERAR DET",
      en: "HOW IT WORKS"
    },
    title: {
      sv: "Från förfrågan till spårbar bokföringsevidens",
      en: "From request to traceable accounting evidence"
    }
  },
  stages: [
    {
      id: "intake",
      title: { sv: "Förfrågan", en: "Request" },
      body: {
        sv:
          "Samla bolagets bokföringsvolym, bankkällor, momsperioder, SIE-filer, rapportbehov och stödjande evidens i rätt Data Room.",
        en:
          "Capture the company's accounting volume, bank sources, VAT periods, SIE files, reporting needs, and supporting evidence in the correct Data Room."
      },
      assetId: "data-room-intake"
    },
    {
      id: "dry_run",
      title: { sv: "Läs rätt underlag", en: "Read the right records" },
      body: {
        sv:
          "Bokföringsagenter läser relevant kontoplan, momskoder, räkenskapsår, huvudbok, banktransaktioner och befintliga rapporter innan förslag förbereds.",
        en:
          "Accounting agents read the relevant chart of accounts, VAT codes, fiscal years, ledger data, bank transactions, and existing reports before preparing suggestions."
      },
      assetId: "data-room-tool-ready"
    },
    {
      id: "preview",
      title: { sv: "Förbered en torrkörning", en: "Prepare a dry-run" },
      body: {
        sv:
          "Förbered bokföringsförslag, importer, momsunderlag, rapporter och SIE-kontroller som förhandsvisningar innan bokföringsposter ändras.",
        en:
          "Prepare posting suggestions, imports, VAT material, reports, and SIE checks as previews before accounting records are changed."
      },
      assetId: "data-room-dry-run"
    },
    {
      id: "confirm",
      title: { sv: "Kontrollera och bekräfta", en: "Check and confirm" },
      body: {
        sv:
          "Granska avvikelser, valideringsresultat, förberedda artefakter, Agent Credit-användning och ansvarsnivå innan nästa steg bekräftas.",
        en:
          "Review discrepancies, validation results, prepared artifacts, Agent Credit usage, and responsibility level before confirming the next step."
      },
      assetId: "data-room-preview"
    },
    {
      id: "evidence_record",
      title: { sv: "Behåll evidensspåret", en: "Keep the evidence record" },
      body: {
        sv:
          "Behåll rapporter, importer, beslut, bekräftelser, inlämningskvittenser och stödjande artefakter som spårbar evidens i Data Room.",
        en:
          "Keep reports, imports, decisions, confirmations, filing receipts, and supporting artifacts available as traceable evidence inside the Data Room."
      },
      assetId: "data-room-evidence"
    },
    {
      id: "handoff",
      title: { sv: "Styr omfattningen", en: "Control the scope" },
      body: {
        sv:
          "Styr migrering, städning, lön, bokslutsarbete, saknad evidens och ansvarskänsliga fall genom rätt servicegranskning innan arbetet fortsätter.",
        en:
          "Route migration, cleanup, payroll, year-end work, missing evidence, and responsibility-sensitive cases through the appropriate service review before work continues."
      },
      assetId: "data-room-confirmed"
    }
  ],
  workstreams: {
    eyebrow: {
      sv: "KOPPLADE ARBETSFLÖDEN",
      en: "CONNECTED WORKSTREAMS"
    },
    title: {
      sv: "Bokföringsområden som finns i Data Room",
      en: "Accounting areas available inside the Data Room"
    },
    lead: {
      sv:
        "Varje arbetsflöde använder samma bolagskontext, evidensspår, agentassisterade förberedelse och bekräftelsekontroller.",
      en:
        "Each workstream uses the same company context, evidence trail, agent-assisted preparation, and confirmation controls."
    }
  },
  modules: [
    {
      key: "bas_and_balances",
      title: { sv: "BAS och saldon", en: "BAS and balances" },
      body: {
        sv: "Läs kontoplan, momskoder, räkenskapsår och saldon innan agenter förbereder bokningar eller rapporter.",
        en: "Read the chart of accounts, VAT codes, fiscal years, and balances before agents prepare postings or reports."
      },
      tag: { sv: "KONTOSTRUKTUR", en: "ACCOUNT STRUCTURE" },
      assetId: "module-chart-balances"
    },
    {
      key: "ledger_and_journals",
      title: { sv: "Huvudbok och verifikationer", en: "Ledger and journals" },
      body: {
        sv: "Använd huvudbok, verifikationslistor, journalrader och drilldown-kontext för bokföringskontroller och kontrollfrågor.",
        en: "Use general ledger, journal lists, journal rows, and drill-down context for accounting checks and control questions."
      },
      tag: { sv: "SPÅRBARHET", en: "TRACEABILITY" },
      assetId: "module-ledger-journals"
    },
    {
      key: "vat_reports",
      title: { sv: "Moms- och rapportartefakter", en: "VAT and report artifacts" },
      body: {
        sv: "Förbered momsrapporter, PDF- och XML-material och stödjande evidens utan att behandla förberedelse som automatisk inlämning.",
        en: "Prepare VAT reports, PDF and XML material, and supporting evidence without treating preparation as automatic filing."
      },
      tag: { sv: "MOMSUNDERLAG", en: "VAT MATERIAL" },
      assetId: "module-vat-reports"
    },
    {
      key: "bank_sources",
      title: { sv: "Bankkällor", en: "Bank sources" },
      body: {
        sv: "Läs banktransaktioner, förbered importer och matchningsförslag och håll undantag synliga tills de bekräftas.",
        en: "Read bank transactions, prepare imports and matching suggestions, and keep exceptions visible until confirmation."
      },
      tag: { sv: "BANKMATCHNING", en: "BANK MATCHING" },
      assetId: "module-bank-records"
    },
    {
      key: "ap_ar",
      title: { sv: "AP/AR", en: "AP/AR" },
      body: {
        sv: "Koppla kund- och leverantörsflöden till fakturastatus, betalningar, förfallodatum, brev och manuell granskning.",
        en: "Connect customer and supplier flows to invoice status, payments, due dates, letters, and manual review."
      },
      tag: { sv: "ÖPPNA POSTER", en: "OPEN ITEMS" },
      assetId: "module-ap-ar"
    },
    {
      key: "sie_migration",
      title: { sv: "SIE-migrering", en: "SIE migration" },
      body: {
        sv: "Förbered SIE-importer, exporter, mappning och dubblettkontroller med avvikelser synliga innan migreringen fortsätter.",
        en: "Prepare SIE imports, exports, mapping, and duplicate checks with discrepancies visible before migration continues."
      },
      tag: { sv: "MIGRERING", en: "MIGRATION" },
      assetId: "module-sie-migration"
    },
    {
      key: "fixed_assets",
      title: { sv: "Anläggningar", en: "Fixed assets" },
      body: {
        sv: "Granska anläggningsregister, avskrivning, nedskrivning, bokföringslänkar och avstämningar som ett separat kontrollerat arbetsflöde.",
        en: "Review fixed-asset records, depreciation, write-downs, accounting links, and reconciliations as a separate controlled workstream."
      },
      tag: { sv: "ANLÄGGNINGSKONTROLL", en: "ASSET CONTROL" },
      assetId: "module-fixed-assets"
    },
    {
      key: "report_artifacts",
      title: { sv: "Rapportregister", en: "Report register" },
      body: {
        sv: "Behåll rapporthistorik, genererade filer, körmetadata, förhandsvisningar och kvittenser för fortsatt leverans och granskning.",
        en: "Keep report history, generated files, run metadata, previews, and receipts available for continued delivery and review."
      },
      tag: { sv: "RAPPORTBEVIS", en: "REPORT EVIDENCE" },
      assetId: "module-report-register"
    }
  ],
  delivery: {
    eyebrow: {
      sv: "LEVERANSMODELL",
      en: "DELIVERY MODEL"
    },
    title: {
      sv: "Programvara, bokföringsagenter och mänsklig granskning i ett arbetsflöde",
      en: "Software, accounting agents, and human review in one workflow"
    }
  },
  deliveryPaths: [
    {
      key: "self_orchestrated_agent",
      title: { sv: "Programåtkomst", en: "Software access" },
      body: {
        sv: "Arbeta direkt i bolagsytan och aktivera bokföringsagenter som kan läsa och förbereda material där stöd finns.",
        en: "Work directly in the company workspace and enable accounting agents to read and prepare material where supported."
      },
      assetId: "delivery-software-first"
    },
    {
      key: "hybrid",
      title: { sv: "Agentassisterade arbetsflöden", en: "Agent-assisted workflows" },
      body: {
        sv: "Bokföringsagenter förbereder torrkörningar, kontroller, utkast och rapportmaterial med Agent Credit-kapacitet kopplad till arbetsytan.",
        en: "Accounting agents prepare dry-runs, checks, drafts, and report material using Agent Credit capacity assigned to the workspace."
      },
      assetId: "delivery-hybrid"
    },
    {
      key: "human_assisted",
      title: { sv: "Mänskligt guidad granskning", en: "Human-guided review" },
      body: {
        sv: "Serviceteamet granskar avvikelser, momsfrågor, saknad evidens, migreringar och ansvarskänsliga fall.",
        en: "The service team reviews discrepancies, VAT questions, missing evidence, migrations, and responsibility-sensitive cases."
      },
      assetId: "delivery-human-assisted"
    },
    {
      key: "review_evidence",
      title: { sv: "Spårbar evidens", en: "Traceable evidence" },
      body: {
        sv: "Beslut, förhandsresultat, rapportfiler, bekräftelser och kvittenser förblir kopplade till bokföringsflödet.",
        en: "Decisions, preview results, report files, confirmations, and receipts remain connected to the accounting workflow."
      },
      assetId: "delivery-review-evidence"
    }
  ],
  controlBoundary: {
    eyebrow: {
      sv: "KONTROLLERAD BOKFÖRINGSLEVERANS",
      en: "CONTROLLED ACCOUNTING DELIVERY"
    },
    title: {
      sv: "Förberedelse hålls separat från bekräftelse och genomförande",
      en: "Preparation stays separate from confirmation and execution"
    },
    body: {
      sv:
        "Bokföringsagenter kan läsa underlag, förbereda torrkörningar och skapa rapporter i Data Room. Ändringar, inlämningar, betalningar och ansvarskänsliga åtgärder kräver rätt bekräftelse, granskning och evidens. SIE-migrering, städning, lön, bokslutsarbete och komplexa avvikelser följer ytterligare omfattningskontroller där det behövs.",
      en:
        "Accounting agents may read records, prepare dry-runs, and generate reports inside the Data Room. Changes, filings, payments, and responsibility-sensitive actions require the appropriate confirmation, review, and evidence. SIE migration, cleanup, payroll, year-end work, and complex discrepancies follow additional scope controls where needed."
    }
  },
  evidenceRules: {
    sv: [
      "Publika DynamicBooks startar inte bokföring eller externa inlämningar; läsningar och rapporter kan förberedas som kontrollunderlag medan ändringar kräver torrkörning, bekräftelse och spårbar evidens.",
      "Momsfiler, SRU/SKV-artefakter och rapportpaket är lokala underlag tills extern inlämning och kvittens finns.",
      "SIE, städning, bokslut, lön, avvikelser och ansvarskänsliga beslut styrs till rätt arbetsbana med omfattningskontroll."
    ],
    en: [
      "The public DynamicBooks site does not start bookkeeping or external filings; reads and reports can be prepared as control material while changes require dry-run, confirmation, and traceable evidence.",
      "VAT files, SRU/SKV artifacts, and report packages remain local material until external filing and receipt exist.",
      "SIE, cleanup, year-end, payroll, discrepancies, and responsibility-sensitive decisions route to the right work lane with scope control."
    ]
  }
};
